import {
  DeleteFilled,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  Button,
  Col,
  Form,
  Input,
  Pagination,
  Row,
  Select,
  Space,
  Table,
  Tooltip,
  theme,
} from "antd";
import { useCallback, useContext, useEffect, useState } from "react";
import Heading from "../../../components/common/heading";
import NotFound from "../../../components/common/notFound";
import ConfirmModal from "../../../components/modal/confirmModal";
import FailModal from "../../../components/modal/failModal";
import SuccessModal from "../../../components/modal/successModal";
import { SUCCESS_CODE } from "../../../lib/const";
import {
  deleteStaff,
  searchStaffManagement,
} from "../../../services/userManagement";
import ActionModal from "./actionModal";
import { SearchStaffContext } from "./searchContext";
import usePositionStaff from "../../../lib/hooks/usePositionStaff";

const Option = Select.Option;
const { useToken } = theme;

const Staff = () => {
  const [searchForm] = Form.useForm();
  const {
    searchValues,
    setSearchValues,
    pageValues,
    setPageValues,
    search,
    setSearch,
  } = useContext(SearchStaffContext);

  const { token } = useToken();
  const [position] = usePositionStaff();
  const [dataSource, setDataSource] = useState([]);
  let [no, setNo] = useState(0);
  const [actionModal, setActionModal] = useState(false);
  const [actionTypeModal, setActionTypeModal] = useState("");
  const [loading, setLoading] = useState(false);
  const [editData, setEditData] = useState({});
  const [fetchData, setFetchData] = useState(false);
  const [successModal, setSuccessModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    type: "",
    show: false,
    id: 0,
  });

  const [failModal, setFailModal] = useState(false);

  const fetchStaff = useCallback(
    async (searchValues, pageValues) => {
      const res = await searchStaffManagement(searchValues, pageValues);

      if (res?.data?.code === SUCCESS_CODE) {
        const { objectValue, pageValue } = res.data;
        setDataSource(objectValue);
        let _p =
          parseInt(pageValues.page) === 0
            ? parseInt(pageValues.page)
            : parseInt(pageValues.page) * pageValues.pageSize;
        setNo(_p);
        setPageValues({
          page: parseInt(pageValue.page),
          pageSize: parseInt(pageValue.pageSize),
          total: parseInt(pageValue.total),
          totalPage: parseInt(pageValue.totalPage),
          sortBy: pageValue.sortBy ? pageValue.sortBy : pageValues.sortBy,
          sortDirection: pageValue.sortDirection
            ? pageValue.sortDirection
            : pageValues.sortDirection,
        });
        setLoading(false);
      } else {
        setDataSource([]);
        setPageValues((prev) => ({
          ...prev,
          page: 0,
          pageSize: 10,
          total: 0,
          totalPage: 0,
        }));
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setDataSource, setSearchValues, setPageValues]
  );

  useEffect(() => {
    if (search) {
      setLoading(true);
      fetchStaff(searchValues, pageValues);
      setSearch(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchStaff, searchValues, pageValues, searchForm, search, setSearch]);

  const handleDeleteStaff = async () => {
    const res = await deleteStaff(confirmModal.id);

    if (res.data.code === SUCCESS_CODE) {
      // setFetchData(true);
      setSearch(true);
    } else {
      setFailModal(true);
    }
    setConfirmModal({
      type: "",
      show: false,
      id: 0,
    });
  };

  useEffect(() => {
    if (fetchData) {
      setLoading(true);
      fetchStaff(searchValues, pageValues);
      setTimeout(() => {
        setSuccessModal(true);
        setFetchData(false);
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchData, fetchStaff]);

  const onSearchForm = (values) => {
    // setLoading(true);
    setSearch(true);
    setSearchValues({
      prefix: values?.prefix || "",
      staffName: values?.staffName || "",
      staffLastname: values?.staffLastname || "",
      fullName: values?.fullName || "",
      positionUniqueId: values?.positionNameTh || "",
    });
  };

  const columns = [
    {
      width: 100,
      align: "center",
      title: "ลำดับ",
      dataIndex: "no",
      key: "no",
      render: (_, record) => <span>{(no += 1)}</span>,
    },
    {
      width: 200,
      title: "คำนำหน้าชื่อ",
      dataIndex: "prefix",
      key: "prefix",
      sorter: (a, b) => a.prefix - b.prefix,
    },
    {
      width: 200,
      title: "ชื่อ",
      dataIndex: "staffName",
      key: "staffName",
      sorter: (a, b) => a.staffName - b.staffName,
    },
    {
      width: 200,
      title: "นามสกุล",
      dataIndex: "staffLastname",
      key: "staffLastname",
      sorter: (a, b) => a.staffLastname - b.staffLastname,
    },
    {
      width: 200,
      title: "ตำแหน่ง",
      dataIndex: "positionTh",
      key: "positionUniqueId",
      sorter: (a, b) => a.positionUniqueId - b.positionUniqueId,
    },
    {
      width: 120,
      align: "center",
      title: "การจัดการ",
      dataIndex: "management",
      key: "management",
      render: (_, record) => (
        <Space>
          <Tooltip placement="bottom" title={"แก้ไข"}>
            <Button
              style={{ backgroundColor: "#FEF1DF", border: 0 }}
              shape="circle"
              onClick={() => {
                setEditData(record);
                setActionModal(true);
              }}
            >
              <EditOutlined style={{ color: "#F79009" }} />
            </Button>
          </Tooltip>
          <Tooltip placement="bottom" title={"ลบ"}>
            <Button
              style={{ backgroundColor: "#FDE7E5", border: 0 }}
              shape="circle"
              onClick={() => {
                setConfirmModal({
                  type: "delete",
                  show: true,
                  id: record.staffUniqueId,
                });
              }}
            >
              <DeleteFilled style={{ color: "#F04438" }} />
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const onSorter = (pagination, filters, sorter) => {
    setSearch(true);
    setPageValues((prev) => ({
      ...prev,
      sortBy: sorter && sorter.order ? sorter.columnKey : "createDate",
      sortDirection:
        sorter && sorter.order
          ? sorter.order === "ascend"
            ? "ASC"
            : "DESC"
          : "ASC",
    }));
  };

  const onPageChange = (page, pageSize) => {
    setNo(no + pageSize);
    setSearch(true);
    setPageValues((prev) => ({
      ...prev,
      page: page - 1,
      pageSize: pageSize,
    }));
  };

  const clearSearchValues = () => {
    setSearch(true);
    searchForm.resetFields();
    setSearchValues({
      fullName: "",
      prefix: "",
      staffName: "",
      staffLastname: "",
      positionUniqueId: "",
    });
    setPageValues({
      page: 0,
      pageSize: 10,
      sortBy: "createDate",
      sortDirection: "DESC",
    });
  };

  return (
    <div className="user_management" style={{ minHeight: "100vh" }}>
      <div className="flex justify-between items-center">
        <Heading text={"การจัดการรายชื่อเจ้าหน้าที่จัดทำรายงาน"} size={4} />
        <div>
          <Button
            type="primary"
            onClick={() => {
              setActionTypeModal("add");
              setActionModal(true);
            }}
          >
            <PlusOutlined /> เพิ่มเจ้าหน้าที่
          </Button>
        </div>
      </div>
      <div className="card">
        <Form form={searchForm} onFinish={onSearchForm} autoComplete="off">
          <Row gutter={[24, 24]}>
            <Col span={8}>
              <Form.Item label="" name="fullName">
                <Input
                  prefix={<SearchOutlined />}
                  placeholder="ชื่อเจ้าหน้าที่"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="" name="staffName">
                <Input placeholder="ชื่อ" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="" name="staffLastname">
                <Input placeholder="นามสกุล" />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item label="" name="positionNameTh">
                <Select
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  placeholder="ตำแหน่ง"
                >
                  {position &&
                    position.map((item) => (
                      <Option value={item.positionUniqueId}>
                        {item.positionNameTh}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={24} align="right">
              <Space>
                <Button onClick={clearSearchValues}>ล้างข้อมูล</Button>
                <Button type="primary" htmlType="submit">
                  ค้นหา
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </div>

      <div className="list_table" style={{ marginTop: 30, marginBottom: 80 }}>
        <Row gutter={[24, 12]}>
          <Col span={24}>
            รายการทั้งหมด{" "}
            <span style={{ color: token.thPrimary, fontWeight: 700 }}>
              {pageValues?.total}
            </span>{" "}
            รายการ
          </Col>
          <Col span={24}>
            <Table
              locale={{ emptyText: <NotFound /> }}
              loading={loading}
              rowKey={"key"}
              dataSource={dataSource}
              columns={columns}
              pagination={false}
              scroll={{
                x: 1301,
              }}
              onChange={onSorter}
            />
          </Col>
          <Col span={24} align="right">
            <Pagination
              showSizeChanger
              defaultCurrent={pageValues?.page + 1 || 1}
              total={pageValues?.total || 10}
              onChange={onPageChange}
              showTotal={(total) => `รวม ${total} รายการ`}
            />
          </Col>
        </Row>
      </div>

      <ActionModal
        open={actionModal}
        setOpen={setActionModal}
        actionTypeModal={actionTypeModal}
        setActionTypeModal={setActionTypeModal}
        position={position}
        editData={editData}
        setEditData={setEditData}
        setFetchData={setFetchData}
      />

      <SuccessModal
        open={successModal}
        setOpen={setSuccessModal}
        title={"สำเร็จ"}
        subTitle={"ทำรายการสำเร็จ"}
      />

      <ConfirmModal
        actionType={"delete"}
        title={"ยืนยันการลบข้อมูล"}
        modalText={"คุณต้องการลบข้อมูล ?"}
        open={confirmModal.type === "delete" ? confirmModal.show : false}
        onOk={() => {
          handleDeleteStaff();
        }}
        onCancel={() => {
          setConfirmModal({
            type: "",
            show: false,
            id: 0,
          });
        }}
      />

      <FailModal
        open={failModal}
        setOpen={setFailModal}
        title={"มีบางอย่างผิดพลาด"}
        subTitle={"มีบางอย่างผิดพลาด โปรดทำรายการอีกครั้ง"}
      />
    </div>
  );
};

export default Staff;
